import React from "react";
import classNames from "classnames";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleQuestion } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";
import { ButtonNew as Button } from "components/buttonNew/index";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import { LocaleCurrency } from "helpers/Locale";
import { IOrderLineItem } from "redux/reducers/models/order";
import { returnedQuantity } from "./orderExchangeDisplay";

interface IOrderLineItemBlockProps {
  item: IOrderLineItem;
  currency?: string;
  discountLines?: Array<any>;
  showReturnedQuantity?: boolean;
  onGiftCardClick?: () => void;
  onTicketDownload?: (lineItemId: number, format?: "zebra") => void;
  onTicketPrint?: (lineItemId: number) => void;
}

export default function OrderLineItemBlock(props: IOrderLineItemBlockProps) {
  const { t } = useTranslation();
  const { item, currency, discountLines = [], showReturnedQuantity } = props;
  const returnedQty = showReturnedQuantity ? returnedQuantity(item) : 0;

  return (
    <>
      <div
        className={classNames("order-line-item", {
          "cursor-pointer": item.product_type === "Gift Card",
        })}
        onClick={item.product_type === "Gift Card" ? props.onGiftCardClick : undefined}
      >
        <div>
          <p className="text-lg text-semibold">{item.product_title}</p>
          {item.product_title !== item.variant_title ? (
            <p className="text-sm text-gray-500">{item.variant_title}</p>
          ) : null}
          {returnedQty > 0 ? (
            <p className="text-sm text-gray-500">
              {t("Returned")}: {returnedQty}
            </p>
          ) : null}
          <div className="flex justify-start gap-2">
            {item.product_type === "Ticket" && props.onTicketDownload && props.onTicketPrint ? (
              <>
                <NavigationDropdownNew
                  label={t("Download")}
                  type="text"
                  leftAlign
                  sections={[
                    [
                      {
                        label: t("Standard Format"),
                        onClick: () => void props.onTicketDownload?.(item.id),
                      },
                      {
                        label: t("Zebra Format"),
                        onClick: () => void props.onTicketDownload?.(item.id, "zebra"),
                      },
                    ],
                  ]}
                />
                <Button type="link" onClick={() => void props.onTicketPrint?.(item.id)}>
                  {t("Print")}
                </Button>
              </>
            ) : null}
          </div>
          <div className="flex flex-col">
            {(item.dynamic_combo_selections ?? []).map(selection => (
              <p key={selection.id} className="text-sm text-gray-500">
                {selection.quantity} {selection.variant?.title ?? ""}
              </p>
            ))}
          </div>
        </div>
        <div className="text-right">
          <p className="text-lg text-gray-500">
            {item.quantity} x <LocaleCurrency currency={currency} amount={item.price} />
          </p>
        </div>
        <div className="text-right">
          <p className="text-lg text-semibold">
            <LocaleCurrency currency={currency} amount={item.price * item.quantity} />
          </p>
        </div>
      </div>

      {discountLines.map((discount: any, i: number) => {
        if (discount.order_line_item_id !== item.id) {
          return null;
        }
        // Loyalty points redemption discounts
        if (discount?.loyalty_points_reward_id && discount?.loyalty_points_used != null) {
          return (
            <div key={i} className="text-right">
              <p className="text-sm text-gray-500">
                [{`-${discount?.loyalty_points_used}${t("pts")}`}
                  <OverlayTrigger placement="auto" overlay={<Tooltip id="leftMenuToolTip">{t("Loyalty reward")}: {discount.title}</Tooltip>}>
                    <FontAwesomeIcon icon={faCircleQuestion} className="ml-1" />
                  </OverlayTrigger>
                ]: <LocaleCurrency currency={currency} amount={-discount.amount} /> 
              </p>
            </div>
          );
        }
        return (
          <div key={i} className="text-right">
            <p className="text-sm text-gray-500">
              {discount.title} - <LocaleCurrency currency={currency} amount={discount.amount} />
              {discount.notes ? (
                <OverlayTrigger placement="auto" overlay={<Tooltip id="leftMenuToolTip">{discount.notes}</Tooltip>}>
                  <FontAwesomeIcon icon={faCircleQuestion} className="ml-2" />
                </OverlayTrigger>
              ) : null}
            </p>
          </div>
        );
      })}

      {item.modifiers ? (
        <div>
          {item.modifiers.map((modifier: any, i: number) => {
            return (
              <div key={i}>
                <div className="order-line-item">
                  <div style={{ color: "#475467", marginLeft: "15px" }}>{modifier.product_title}</div>
                  <div className="text-right">
                    <p className="text-lg text-gray-500">
                      {modifier.quantity} x <LocaleCurrency currency={currency} amount={modifier.price} />
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg text-semibold">
                      <LocaleCurrency currency={currency} amount={modifier.price * modifier.quantity} />
                    </p>
                  </div>
                </div>

                {modifier.total_discount !== 0 ? (
                  <div>
                    {discountLines.map((discount: any, discountIndex: number) => {
                      if (discount.order_line_item_id !== modifier.id) {
                        return null;
                      }
                      return (
                        <div key={discountIndex} className="text-right">
                          <p className="text-sm text-gray-500">
                            {discount.title} - <LocaleCurrency currency={currency} amount={discount.amount} />
                            {discount.notes ? (
                              <OverlayTrigger
                                placement="auto"
                                overlay={<Tooltip id="leftMenuToolTip">{discount.notes}</Tooltip>}
                              >
                                <FontAwesomeIcon icon={faCircleQuestion} className="ml-2" />
                              </OverlayTrigger>
                            ) : null}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      {item.total_discount !== 0 && (
        <div className="text-right">
          <p className="text-lg text-semibold">
            <LocaleCurrency currency={currency} amount={item.subtotal_price} />
          </p>
        </div>
      )}
    </>
  );
}
